// ── Pet bonding types (v2 Softening) ──
//
// Canonical source: docs/design/pet-bonding.md
// Pure data types for the pet-bonding system. No runtime deps.
/** All 20 dimension keys as a readonly array for iteration. */
export const PET_DIMENSION_KEYS = [
    'combatSeconds',
    'cookSeconds',
    'shrineSeconds',
    'hearthSeconds',
    'dreamingSeconds',
    'expeditionSeconds',
    'companionHearthSeconds',
    'companionShrineSeconds',
    'rainSeconds',
    'clearSeconds',
    'daySeconds',
    'nightSeconds',
    'verdanaSeconds',
    'sablewindSeconds',
    'frostmereSeconds',
    'embermireSeconds',
    'travelSeconds',
    'stillSeconds',
    'fetchSeconds',
    'ritualSeconds',
];
/** Creates a zeroed accumulator. */
export function emptyAccumulator() {
    return {
        combatSeconds: 0,
        cookSeconds: 0,
        shrineSeconds: 0,
        hearthSeconds: 0,
        dreamingSeconds: 0,
        expeditionSeconds: 0,
        companionHearthSeconds: 0,
        companionShrineSeconds: 0,
        rainSeconds: 0,
        clearSeconds: 0,
        daySeconds: 0,
        nightSeconds: 0,
        verdanaSeconds: 0,
        sablewindSeconds: 0,
        frostmereSeconds: 0,
        embermireSeconds: 0,
        travelSeconds: 0,
        stillSeconds: 0,
        fetchSeconds: 0,
        ritualSeconds: 0,
    };
}
/** Bonding threshold in seconds (100 real hours). */
export const HATCH_THRESHOLD_SECONDS = 360_000;
/** Fallback form when all dimensions are zero. */
export const FALLBACK_FORM = 'paper-folded-bird';
/**
 * The 24-entry form library with signature vectors.
 * Weights are tunable; current values are a first pass per the design doc.
 * Positive weight = "this dimension pulls toward this form."
 * Negative weight = "this dimension pushes away from this form."
 */
export const PET_FORM_LIBRARY = [
    {
        id: 'moss-kodama',
        concept: 'Small moss-grown spirit, round skull, hollow eyes.',
        moodTag: 'quiet, still, ancient',
        signature: { shrineSeconds: 1.5, verdanaSeconds: 1.2, stillSeconds: 1.0, dreamingSeconds: 0.8, combatSeconds: -1.5 },
    },
    {
        id: 'soot-tumbler',
        concept: 'Cloud of soft black fluff with two shining eyes.',
        moodTag: 'mischievous, warm, communal',
        signature: { hearthSeconds: 1.2, companionHearthSeconds: 1.5, nightSeconds: 0.8, embermireSeconds: 1.0 },
    },
    {
        id: 'lantern-moth',
        concept: 'Papery wings, a small interior glow.',
        moodTag: 'night-loving, wandering',
        signature: { nightSeconds: 1.5, ritualSeconds: 1.0, travelSeconds: 1.2, shrineSeconds: 0.6, stillSeconds: -1.0 },
    },
    {
        id: 'pot-warming-cat-shape',
        concept: 'Loaf-posture, paws tucked, eyes half-closed.',
        moodTag: 'domestic, patient, soft',
        signature: { cookSeconds: 1.5, hearthSeconds: 1.2, companionHearthSeconds: 1.0, expeditionSeconds: -1.0, combatSeconds: -1.0 },
    },
    {
        id: 'river-turtle-small',
        concept: 'Low, slow, unbothered. Mossy shell.',
        moodTag: 'steady, witnessing',
        signature: { shrineSeconds: 1.0, rainSeconds: 1.2, verdanaSeconds: 1.0, stillSeconds: 1.0, dreamingSeconds: 0.8 },
    },
    {
        id: 'paper-folded-bird',
        concept: 'Origami crane that moves like it is alive.',
        moodTag: 'fragile, wandering, kind',
        signature: { ritualSeconds: 1.0, travelSeconds: 1.0, clearSeconds: 0.8, companionHearthSeconds: 0.6 },
    },
    {
        id: 'ember-finch',
        concept: 'Small bird with coals in its feathers.',
        moodTag: 'bright, restless, warm',
        signature: { embermireSeconds: 1.5, daySeconds: 1.0, travelSeconds: 1.2, frostmereSeconds: -1.0 },
    },
    {
        id: 'dune-fennec-shape',
        concept: 'Large ears, long wary body, desert-pale coat.',
        moodTag: 'watchful, patient with heat',
        signature: { sablewindSeconds: 1.5, stillSeconds: 1.0, shrineSeconds: 0.8, nightSeconds: 0.8 },
    },
    {
        id: 'frost-hare',
        concept: 'White hare, long pink-inner-ears, snow-dust fur.',
        moodTag: 'gentle, vigilant, cold-loving',
        signature: { frostmereSeconds: 1.5, dreamingSeconds: 1.0, companionHearthSeconds: 0.8 },
    },
    {
        id: 'bog-newt',
        concept: 'Small salamander-shape, glossy, amber-eyed.',
        moodTag: 'curious, wet, unhurried',
        signature: { rainSeconds: 1.5, verdanaSeconds: 1.0, fetchSeconds: 1.2, stillSeconds: -1.0 },
    },
    {
        id: 'thaw-otter',
        concept: 'River otter shape, slick, playful.',
        moodTag: 'sociable, water-loving',
        signature: { frostmereSeconds: 1.2, companionHearthSeconds: 1.0, travelSeconds: 1.0, daySeconds: 0.8 },
    },
    {
        id: 'ash-cricket',
        concept: 'Long-legged insect that hums.',
        moodTag: 'musical, solitary',
        signature: { embermireSeconds: 1.2, ritualSeconds: 1.5, nightSeconds: 1.0, companionHearthSeconds: -1.0 },
    },
    {
        id: 'meadow-fox-kit',
        concept: 'Young fox, white-tipped tail, soft ears.',
        moodTag: 'curious, roaming, warm',
        signature: { verdanaSeconds: 1.5, travelSeconds: 1.2, daySeconds: 1.0, clearSeconds: 0.8 },
    },
    {
        id: 'cave-firefly-swarm',
        concept: 'Loose cloud of small lights; behaves as one entity.',
        moodTag: 'many-as-one, communal',
        signature: { expeditionSeconds: 1.5, nightSeconds: 1.0, companionHearthSeconds: 1.0, ritualSeconds: 0.8 },
    },
    {
        id: 'hearthworm-spirit',
        concept: 'Fat glowworm with a sleepy face.',
        moodTag: 'hearth-bound, easy',
        signature: { hearthSeconds: 1.5, cookSeconds: 1.2, stillSeconds: 1.0, travelSeconds: -1.0, expeditionSeconds: -1.0 },
    },
    {
        id: 'pilgrim-tortoise',
        concept: 'Larger turtle with a lantern on its shell.',
        moodTag: 'long-walking, patient, wise',
        signature: { travelSeconds: 1.2, shrineSeconds: 1.0, ritualSeconds: 1.0, stillSeconds: 0.6, clearSeconds: 0.6 },
    },
    {
        id: 'scar-wolf-kit',
        concept: 'Small wolf-shape with a single healed notch in one ear.',
        moodTag: 'loyal, watchful, scarred',
        signature: { expeditionSeconds: 1.5, combatSeconds: 1.2, companionHearthSeconds: 1.0, nightSeconds: 0.8 },
    },
    {
        id: 'snow-kodama',
        concept: 'White kodama, head tilts with a clicking sound.',
        moodTag: 'ancient, cold, curious',
        signature: { frostmereSeconds: 1.5, shrineSeconds: 1.2, stillSeconds: 1.0, dreamingSeconds: 0.8 },
    },
    {
        id: 'rainpetal',
        concept: 'Pale flower with a face; opens when wet.',
        moodTag: 'shy, weather-loving',
        signature: { rainSeconds: 1.5, verdanaSeconds: 1.0, stillSeconds: 0.8, daySeconds: 0.6 },
    },
    {
        id: 'dream-fish',
        concept: 'Small fish that swims through air near the owner.',
        moodTag: 'dreaming, quiet, companion-seeking',
        signature: { dreamingSeconds: 1.5, hearthSeconds: 1.0, companionHearthSeconds: 0.8, clearSeconds: 0.6 },
    },
    {
        id: 'vine-sprite',
        concept: 'Thin vine that walks on root-legs; leaf-haired.',
        moodTag: 'growing, patient, rooted-but-moving',
        signature: { verdanaSeconds: 1.2, ritualSeconds: 1.0, rainSeconds: 1.0, travelSeconds: 0.8 },
    },
    {
        id: 'sand-djinn-wisp',
        concept: 'A small column of warm sand that has learned to have a face.',
        moodTag: 'restless, warm, desert-loving',
        signature: { sablewindSeconds: 1.5, daySeconds: 1.0, travelSeconds: 1.2, clearSeconds: 0.8 },
    },
    {
        id: 'ember-salamander',
        concept: 'Small lizard with a crest of slow fire.',
        moodTag: 'quiet heat, forge-loving',
        signature: { embermireSeconds: 1.5, cookSeconds: 1.2, ritualSeconds: 0.8, stillSeconds: 0.6 },
    },
    {
        id: 'starling-shape',
        concept: 'Flock-bird that prefers to be in pairs.',
        moodTag: 'sociable, watchful',
        signature: { companionHearthSeconds: 1.5, companionShrineSeconds: 1.2, travelSeconds: 1.0, daySeconds: 0.8 },
    },
];
/** Trait adjective pairs for journal text. */
export const TRAIT_ADJECTIVES = {
    brave: { positive: 'brave', negative: 'wary' },
    gentle: { positive: 'gentle', negative: 'fierce' },
    wise: { positive: 'wise', negative: 'simple' },
    sociable: { positive: 'sociable', negative: 'solitary' },
    curious: { positive: 'curious', negative: 'content' },
    stubborn: { positive: 'stubborn', negative: 'yielding' },
    shy: { positive: 'shy', negative: 'open' },
    loyal: { positive: 'loyal', negative: 'wandering' },
    playful: { positive: 'playful', negative: 'serious' },
    melancholy: { positive: 'melancholy', negative: 'bright' },
};
/** Threshold for surfacing a trait adjective in the journal. */
export const TRAIT_SURFACE_THRESHOLD = 0.4;
/** Max adjectives shown in pet journal entry. */
export const MAX_SURFACE_ADJECTIVES = 3;
/**
 * Trait weight table. Each trait is a weighted sum of normalized dimensions.
 * Values tuned per pet-bonding.md §5.
 */
export const TRAIT_WEIGHTS = {
    brave: { combatSeconds: 1.2, expeditionSeconds: 1.0, travelSeconds: 0.8, stillSeconds: -0.8 },
    gentle: { cookSeconds: 1.0, hearthSeconds: 0.8, rainSeconds: 0.6, combatSeconds: -1.2 },
    wise: { shrineSeconds: 1.0, ritualSeconds: 0.8, dreamingSeconds: 0.8, stillSeconds: 0.6 },
    sociable: { companionHearthSeconds: 1.2, companionShrineSeconds: 1.0, hearthSeconds: 0.6 },
    curious: { travelSeconds: 1.0, fetchSeconds: 1.0, expeditionSeconds: 0.8, stillSeconds: -0.8 },
    stubborn: { stillSeconds: 1.0, ritualSeconds: 0.8 },
    shy: { rainSeconds: 0.8, nightSeconds: 0.8, companionHearthSeconds: -0.8 },
    loyal: { hearthSeconds: 1.2, companionHearthSeconds: 0.6 },
    playful: { travelSeconds: 1.0, daySeconds: 0.8, companionHearthSeconds: 0.6, clearSeconds: 0.6 },
    melancholy: { nightSeconds: 1.0, rainSeconds: 0.8, frostmereSeconds: 0.6, dreamingSeconds: 0.8 },
};
/** Trait-gated passive behaviors from pet-bonding.md §6.2. */
export const PET_PASSIVE_BEHAVIORS = [
    { id: 'greets-owner', description: 'Greets owner at the door', trait: 'loyal', threshold: 0.4 },
    { id: 'follows-visitors', description: 'Follows visitors around the home', trait: 'curious', threshold: 0.5 },
    { id: 'hides-from-visitors', description: 'Hides from visitors in the basket', trait: 'shy', threshold: 0.5 },
    { id: 'brings-whispers', description: 'Occasionally brings a Whisper on owner login', trait: 'wise', threshold: 0.4 },
    { id: 'sits-with-companions', description: 'Sits with visiting companion pets', trait: 'sociable', threshold: 0.5 },
    { id: 'keeps-hearth-warm', description: 'Sits beside the hearth when owner is away', trait: 'loyal', threshold: 0.5 },
    { id: 'barks-at-ghosts', description: 'Chirps at ghost players near the home', trait: 'brave', threshold: 0.5 },
    { id: 'naps-unexpected', description: 'Naps in unexpected places around the home', trait: 'playful', threshold: 0.4 },
    { id: 'watches-storms', description: 'Watches the sky at the window during storms', trait: 'melancholy', threshold: 0.5 },
];
// ── Diegetic cues (pet-bonding.md §2.4) ─────────────────────────────
/** Bonding progress diegetic cue thresholds. */
export const BONDING_CUES = [
    { maxSeconds: 18_000, text: 'The egg is warm.' },
    { maxSeconds: 90_000, text: 'The egg hums faintly when you are near.' },
    { maxSeconds: 180_000, text: 'The egg shifts when you sleep.' },
    { maxSeconds: 270_000, text: 'The egg has stopped shifting. Like it is listening.' },
    { maxSeconds: 342_000, text: 'The egg feels close to something. You don\'t know what.' },
    { maxSeconds: 359_999, text: 'Soon. Soon.' },
];
//# sourceMappingURL=types.js.map